import type { PageResult } from '@jiaofa/api-types'
import { request } from './request'

export interface AdminCoupon {
  id: string
  name: string
  type?: string
  amount?: number
  discountRate?: number | string
  threshold?: number
  validType?: string
  validFrom?: string
  validTo?: string
  validDays?: number
  totalCount?: number
  issuedCount?: number
  perUserLimit?: number
  storeIds?: string
  productScope?: string
  status?: string
  createdAt?: string
}

export interface AdminBanner {
  id: string
  title: string
  imageUrl: string
  linkType?: string
  linkValue?: string
  sort?: number
  enabled?: boolean
  startAt?: string
  endAt?: string
}

export interface BannerSavePayload {
  id?: string
  title: string
  imageUrl: string
  linkType?: string
  linkValue?: string
  sort?: number
  enabled?: boolean
  startAt?: string
  endAt?: string
}

export interface CouponSavePayload {
  id?: string
  name: string
  type?: string
  amount?: number
  discountRate?: number | string
  threshold?: number
  validType?: string
  validFrom?: string
  validTo?: string
  validDays?: number
  totalCount?: number
  perUserLimit?: number
  storeIds?: string
  productScope?: string
  status?: string
}

export interface CouponIssuePayload {
  couponId: string
  targetType?: string
  targetIds?: string[]
  count?: number
}

export interface AdminRechargePackage {
  id: string
  name: string
  amount?: number
  giftBalance?: number
  giftCouponTemplateIds?: string
  sort?: number
  status?: string
}

export interface RechargePackageSavePayload {
  id?: string
  name: string
  amount?: number
  giftBalance?: number
  giftCouponTemplateIds?: string
  sort?: number
  enabled?: boolean
}

export interface AdminCardTemplate {
  id: string
  name: string
  category?: string
  salePrice?: number
  faceValue?: number
  validDays?: number
  countValue?: number
  benefit?: string
  totalCount?: number
  issuedCount?: number
  perUserLimit?: number
  storeIds?: string
  status?: string
  createdAt?: string
}

export interface CardTemplateSavePayload {
  id?: string
  name: string
  category?: string
  salePrice?: number
  faceValue?: number
  validDays?: number
  countValue?: number
  benefit?: string
  totalCount?: number
  perUserLimit?: number
  storeIds?: string
  status?: string
}

export interface FlashSaleItem {
  productId: string
  productName?: string
  skuId?: string
  salePrice?: number
  limitQuantity?: number
  stock?: number
  sold?: number
}

export interface AdminFlashSale {
  id: string
  name: string
  storeId?: string
  /** 后端按 storeIds 同序拼好的门店名，列表优先展示它，缺失才回退门店 ID */
  storeNames?: string
  startAt?: string
  endAt?: string
  status?: string
  items?: FlashSaleItem[]
}

export interface FlashSaleSavePayload {
  id?: string
  name: string
  storeId?: string
  startAt?: string
  endAt?: string
  status?: string
  items?: FlashSaleItem[]
}

export interface AdminGroupBuy {
  id: string
  name: string
  productId?: string
  productName?: string
  groupPrice?: number
  teamSize?: number
  durationHours?: number
  storeIds?: string
  storeNames?: string
  status?: string
  startAt?: string
  endAt?: string
}

export interface GroupBuySavePayload {
  id?: string
  name: string
  productId?: string
  groupPrice?: number
  teamSize?: number
  durationHours?: number
  storeIds?: string
  status?: string
  startAt?: string
  endAt?: string
}

export interface AdminBargainActivity {
  id: string
  name: string
  productId?: string
  productName?: string
  floorPrice?: number
  initPrice?: number
  helpTimes?: number
  storeId?: string
  status?: string
}

export interface BargainSavePayload {
  id?: string
  name: string
  productId?: string
  floorPrice?: number
  initPrice?: number
  helpTimes?: number
  storeId?: string
  status?: string
}

export interface AdminInviteConfig {
  id: string
  scene?: string
  inviterCouponTemplateId?: string
  inviteeCouponTemplateId?: string
  maxTimes?: number
  enabled?: boolean
}

export interface InviteConfigSavePayload {
  id?: string
  scene?: string
  inviterCouponTemplateId?: string
  inviteeCouponTemplateId?: string
  maxTimes?: number
  enabled?: boolean
}

export const marketingApiReady = true as const

export const fetchBanners = async (): Promise<AdminBanner[]> =>
  request<AdminBanner[]>({
    url: '/marketing/banners',
    method: 'GET',
  })

export const createBanner = async (payload: BannerSavePayload): Promise<void> =>
  request<void, BannerSavePayload>({
    url: '/marketing/banners',
    method: 'POST',
    data: payload,
  })

export const updateBanner = async (payload: BannerSavePayload): Promise<void> =>
  request<void, BannerSavePayload>({
    url: '/marketing/banners',
    method: 'PUT',
    data: payload,
  })

export const deleteBanner = async (id: string): Promise<void> =>
  request<void>({
    url: `/marketing/banners/${id}`,
    method: 'DELETE',
  })

export const fetchCoupons = async (params: { page?: number; pageSize?: number }): Promise<PageResult<AdminCoupon>> =>
  request<PageResult<AdminCoupon>>({
    url: '/coupons',
    method: 'GET',
    params,
  })

export const createCoupon = async (payload: CouponSavePayload): Promise<void> =>
  request<void, CouponSavePayload>({
    url: '/coupons',
    method: 'POST',
    data: payload,
  })

export const updateCoupon = async (payload: CouponSavePayload): Promise<void> =>
  request<void, CouponSavePayload>({
    url: '/coupons',
    method: 'PUT',
    data: payload,
  })

export const deleteCoupon = async (id: string): Promise<void> =>
  request<void>({
    url: `/coupons/${id}`,
    method: 'DELETE',
  })

export const issueCoupon = async (payload: CouponIssuePayload): Promise<void> =>
  request<void, CouponIssuePayload>({
    url: '/coupons/issue',
    method: 'POST',
    data: payload,
  })

export const fetchRechargePackages = async (): Promise<AdminRechargePackage[]> =>
  request<AdminRechargePackage[]>({
    url: '/recharge-packages',
    method: 'GET',
  })

export const createRechargePackage = async (payload: RechargePackageSavePayload): Promise<void> =>
  request<void, RechargePackageSavePayload>({
    url: '/recharge-packages',
    method: 'POST',
    data: payload,
  })

export const updateRechargePackage = async (payload: RechargePackageSavePayload): Promise<void> =>
  request<void, RechargePackageSavePayload>({
    url: '/recharge-packages',
    method: 'PUT',
    data: payload,
  })

export const deleteRechargePackage = async (id: string): Promise<void> =>
  request<void>({
    url: `/recharge-packages/${id}`,
    method: 'DELETE',
  })

export const fetchCardTemplates = async (): Promise<AdminCardTemplate[]> =>
  request<AdminCardTemplate[]>({
    url: '/card-templates',
    method: 'GET',
  })

export const createCardTemplate = async (payload: CardTemplateSavePayload): Promise<void> =>
  request<void, CardTemplateSavePayload>({
    url: '/card-templates',
    method: 'POST',
    data: payload,
  })

export const updateCardTemplate = async (payload: CardTemplateSavePayload): Promise<void> =>
  request<void, CardTemplateSavePayload>({
    url: '/card-templates',
    method: 'PUT',
    data: payload,
  })

export const deleteCardTemplate = async (id: string): Promise<void> =>
  request<void>({
    url: `/card-templates/${id}`,
    method: 'DELETE',
  })

export const fetchGroupBuys = async (): Promise<AdminGroupBuy[]> =>
  request<AdminGroupBuy[]>({
    url: '/marketing/group-buy',
    method: 'GET',
  })

export const createGroupBuy = async (payload: GroupBuySavePayload): Promise<void> =>
  request<void, GroupBuySavePayload>({
    url: '/marketing/group-buy',
    method: 'POST',
    data: payload,
  })

export const updateGroupBuy = async (payload: GroupBuySavePayload): Promise<void> =>
  request<void, GroupBuySavePayload>({
    url: '/marketing/group-buy',
    method: 'PUT',
    data: payload,
  })

export const deleteGroupBuy = async (id: string): Promise<void> =>
  request<void>({
    url: `/marketing/group-buy/${id}`,
    method: 'DELETE',
  })

export const fetchBargains = async (): Promise<AdminBargainActivity[]> =>
  request<AdminBargainActivity[]>({
    url: '/marketing/bargain',
    method: 'GET',
  })

export const createBargain = async (payload: BargainSavePayload): Promise<void> =>
  request<void, BargainSavePayload>({
    url: '/marketing/bargain',
    method: 'POST',
    data: payload,
  })

export const updateBargain = async (payload: BargainSavePayload): Promise<void> =>
  request<void, BargainSavePayload>({
    url: '/marketing/bargain',
    method: 'PUT',
    data: payload,
  })

export const deleteBargain = async (id: string): Promise<void> =>
  request<void>({
    url: `/marketing/bargain/${id}`,
    method: 'DELETE',
  })

export const fetchFlashSales = async (): Promise<AdminFlashSale[]> =>
  request<AdminFlashSale[]>({
    url: '/marketing/flash-sale',
    method: 'GET',
  })

export const createFlashSale = async (payload: FlashSaleSavePayload): Promise<void> =>
  request<void, FlashSaleSavePayload>({
    url: '/marketing/flash-sale',
    method: 'POST',
    data: payload,
  })

export const updateFlashSale = async (payload: FlashSaleSavePayload): Promise<void> =>
  request<void, FlashSaleSavePayload>({
    url: '/marketing/flash-sale',
    method: 'PUT',
    data: payload,
  })

export const deleteFlashSale = async (id: string): Promise<void> =>
  request<void>({
    url: `/marketing/flash-sale/${id}`,
    method: 'DELETE',
  })

export const fetchInviteConfigs = async (): Promise<AdminInviteConfig[]> =>
  request<AdminInviteConfig[]>({
    url: '/marketing/invite',
    method: 'GET',
  })

export const createInviteConfig = async (payload: InviteConfigSavePayload): Promise<void> =>
  request<void, InviteConfigSavePayload>({
    url: '/marketing/invite',
    method: 'POST',
    data: payload,
  })

export const updateInviteConfig = async (payload: InviteConfigSavePayload): Promise<void> =>
  request<void, InviteConfigSavePayload>({
    url: '/marketing/invite',
    method: 'PUT',
    data: payload,
  })

export const deleteInviteConfig = async (id: string): Promise<void> =>
  request<void>({
    url: `/marketing/invite/${id}`,
    method: 'DELETE',
  })
